import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Printer, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const { tr } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.email) navigate("/admin/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back!");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || tr("admin_invalid"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-950 cp-grid-texture px-4">
      <div className="w-full max-w-sm">
        <Link to="/" className="mb-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white">
          <ArrowLeft size={16} /> Back to website
        </Link>
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-8 shadow-2xl">
          <div className="flex items-center gap-2.5">
            <span className="grid h-10 w-10 place-items-center rounded-md bg-primary text-white"><Printer size={22} /></span>
            <div>
              <h1 className="font-heading text-xl font-extrabold text-white">{tr("admin_title")}</h1>
              <p className="text-xs text-slate-400">{tr("admin_sub")}</p>
            </div>
          </div>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-login-form">
            <div>
              <Label className="text-slate-300">{tr("admin_email")}</Label>
              <Input
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 bg-slate-800 border-slate-700 text-white"
                placeholder="admin@creativeprints.in"
                required
              />
            </div>
            <div>
              <Label className="text-slate-300">{tr("admin_password")}</Label>
              <Input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-slate-800 border-slate-700 text-white"
                placeholder="••••••••"
                required
              />
            </div>
            <Button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full gap-2 bg-primary hover:bg-amber-700 text-white font-semibold h-11">
              {loading ? <><Loader2 size={18} className="animate-spin" /> …</> : tr("admin_login")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
